import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGridFilter, type CustomFilterProps } from "ag-grid-react";

/**
 * Custom AG Grid "set filter" — checkbox list of unique values.
 * Community-tier alternative to the Enterprise agSetColumnFilter.
 *
 * Model shape: string[] (selected values) or null (no filter / all selected).
 */
export default function SetFilter({
  model,
  onModelChange,
  getValue,
  rowModel,
}: CustomFilterProps<any, any, string[]>) {
  const allValues = useMemo(() => {
    const vals = new Set<string>();
    rowModel.forEachNode((node) => {
      const v = getValue(node);
      if (v != null) vals.add(String(v));
    });
    return Array.from(vals).sort();
  }, [rowModel, getValue]);

  const selected = useMemo<Set<string>>(
    () => new Set(model ?? allValues),
    [model, allValues],
  );

  const doesFilterPass = useCallback(
    ({ node }: { node: any }) => {
      if (!model) return true;
      const v = getValue(node);
      return model.includes(String(v));
    },
    [model, getValue],
  );

  useGridFilter({ doesFilterPass });

  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Optimistic checkbox state shown instantly while the 200ms commit is pending.
  const pendingRef = useRef<Record<string, boolean>>({});
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  // Which row just got clicked, for the brief highlight feedback.
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    },
    [],
  );

  const commit = useCallback(() => {
    const next = new Set(selected);
    Object.entries(pendingRef.current).forEach(([v, checked]) => {
      if (checked) next.add(v);
      else next.delete(v);
    });
    pendingRef.current = {};
    setOptimistic({});
    setFlash(null);
    if (next.size === allValues.length || next.size === 0) {
      onModelChange(null);
    } else {
      onModelChange(Array.from(next));
    }
  }, [selected, allValues, onModelChange]);

  const toggle = (val: string) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    // Flip the checkbox immediately for instant visual feedback...
    const current = pendingRef.current[val] ?? selected.has(val);
    pendingRef.current = { ...pendingRef.current, [val]: !current };
    setOptimistic({ ...pendingRef.current });
    setFlash(val);
    // ...but defer the actual filter change so a double-click can cancel it.
    clickTimer.current = setTimeout(commit, 200);
  };

  const isolate = (val: string) => {
    if (clickTimer.current) clearTimeout(clickTimer.current);
    pendingRef.current = {};
    setOptimistic({});
    setFlash(null);
    const alreadyIsolated = model?.length === 1 && model[0] === val;
    onModelChange(alreadyIsolated ? null : [val]);
  };

  const allChecked = selected.size === allValues.length;

  return (
    <div style={{ padding: "8px 12px", minWidth: 160, fontSize: "0.82rem" }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 6,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={allChecked}
          onChange={() => onModelChange(allChecked ? [] : null)}
        />
        (Select All)
      </label>
      <hr style={{ margin: "4px 0" }} />
      {allValues.map((val) => (
        <label
          key={val}
          title="Double-click to isolate"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "2px 4px",
            margin: "0 -4px",
            borderRadius: 3,
            cursor: "pointer",
            backgroundColor: flash === val ? "#e7f1ff" : "transparent",
            transition: "background-color 0.15s ease",
          }}
          onDoubleClick={(e) => {
            e.preventDefault();
            isolate(val);
          }}
        >
          <input
            type="checkbox"
            checked={optimistic[val] ?? selected.has(val)}
            onChange={() => toggle(val)}
          />
          {val}
        </label>
      ))}
    </div>
  );
}
